/**
 * WorkspaceSettingsTab Component
 *
 * Tab for managing workspace settings (name and token limits).
 * Token limits can only be edited by superadmins.
 * Workspace cannot be deleted.
 */

import React, { useState, useEffect } from 'react';
import { Workspace } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Save, Zap } from 'lucide-react';

interface WorkspaceSettingsTabProps {
  workspace: Workspace;
}

// Format number with thousands separator (utility function)
const formatNumber = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  // Format with dots as thousands separator
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Parse formatted number to integer (utility function)
const parseFormattedNumber = (value: string): number => {
  return parseInt(value.replace(/\./g, ''), 10);
};

export const WorkspaceSettingsTab: React.FC<WorkspaceSettingsTabProps> = ({ workspace }) => {
  const { updateWorkspace } = useWorkspace();
  const { user: currentUser } = useAuth();
  const [name, setName] = useState(workspace.name);
  const [monthlyTokenLimit, setMonthlyTokenLimit] = useState<string>(formatNumber(workspace.monthly_token_limit.toString()));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Only TD users can edit workspace token limits
  const canEditTokenLimit = currentUser?.isSuperAdmin || false;

  // Sync local state with workspace prop changes
  useEffect(() => {
    setName(workspace.name);
    setMonthlyTokenLimit(formatNumber(workspace.monthly_token_limit.toString()));
  }, [workspace.name, workspace.monthly_token_limit]);

  const handleTokenLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // Only allow digits and dots
    if (rawValue === '' || /^[\d.]*$/.test(rawValue)) {
      setMonthlyTokenLimit(formatNumber(rawValue));
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError('Nome do workspace é obrigatório');
      return;
    }

    // Validate token limit if being edited
    const tokenLimit = parseFormattedNumber(monthlyTokenLimit);
    if (canEditTokenLimit) {
      if (isNaN(tokenLimit) || tokenLimit < 1000) {
        setError('Limite de tokens deve ser no mínimo 1.000 tokens');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const updateData: { name: string; monthly_token_limit?: number } = {
        name: name.trim(),
      };

      // Only include token limit if user has permission to edit it
      if (canEditTokenLimit) {
        updateData.monthly_token_limit = tokenLimit;
      }

      await updateWorkspace(workspace.id, updateData);

      setSuccess('Workspace atualizado com sucesso');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar workspace');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tokenUsagePercentage = (workspace.token_used / workspace.monthly_token_limit) * 100;

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Edit Form - MOVED TO TOP */}
      <form onSubmit={handleUpdate} className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Editar Nome do Workspace</h3>

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-[#cccccc] mb-2">
            Nome do Workspace
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            placeholder="Ex: Minha Empresa Ltda"
            className="
              w-full px-4 py-3
              bg-[#1a1a1a] border border-[#333333]
              text-white placeholder-[#888888]
              rounded-lg
              focus:outline-none focus:border-[#F4CC04]
              disabled:opacity-50
            "
            maxLength={255}
            required
          />
          <p className="mt-1 text-xs text-[#888888]">
            Nome da sua organização/empresa
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="
              flex items-center gap-2 px-4 py-2
              bg-[#F4CC04] hover:bg-[#D4B104]
              text-black font-medium
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            <Save className="w-4 h-4" />
            <span>{isSubmitting ? 'Salvando...' : 'Salvar Alterações'}</span>
          </button>
        </div>
      </form>

      {/* Workspace Info */}
      <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#333333]">
        <h3 className="text-lg font-semibold text-white mb-3">Informações do Workspace</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-[#888888]">ID do Workspace:</span>
            <span className="ml-2 font-medium text-white">{workspace.id}</span>
          </div>
          <div>
            <span className="text-[#888888]">ID do Proprietário:</span>
            <span className="ml-2 font-medium text-white">{workspace.owner_id}</span>
          </div>
          <div>
            <span className="text-[#888888]">Criado em:</span>
            <span className="ml-2 font-medium text-white">
              {new Date(workspace.created_at).toLocaleDateString('pt-BR')}
            </span>
          </div>
          <div>
            <span className="text-[#888888]">Atualizado em:</span>
            <span className="ml-2 font-medium text-white">
              {new Date(workspace.updated_at).toLocaleDateString('pt-BR')}
            </span>
          </div>
        </div>
      </div>

      {/* Token Limit Management */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">Gestão de Tokens</h3>

        {/* Current Usage Display */}
        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4 mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[#888888]">Usado este mês</span>
            <span className="font-medium text-white">
              {workspace.token_used.toLocaleString()} / {workspace.monthly_token_limit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-[#0a0a0a] rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                tokenUsagePercentage >= 90
                  ? 'bg-red-500'
                  : tokenUsagePercentage >= 70
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(tokenUsagePercentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-[#888888] mt-2">
            {tokenUsagePercentage.toFixed(1)}% do limite mensal utilizado
          </p>
        </div>

        {/* Token Limit Editor (TD Users Only) */}
        {canEditTokenLimit && (
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <Zap className="w-5 h-5 text-[#F4CC04] mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-white">Limite Mensal de Tokens</h4>
                <p className="text-xs text-[#888888] mt-1">
                  Apenas usuários da TD podem editar o limite de tokens do workspace
                </p>
              </div>
            </div>
            <div>
              <label htmlFor="tokenLimit" className="block text-sm font-medium text-[#cccccc] mb-2">
                Limite Mensal (tokens)
              </label>
              <input
                id="tokenLimit"
                type="text"
                inputMode="numeric"
                value={monthlyTokenLimit}
                onChange={handleTokenLimitChange}
                disabled={isSubmitting}
                placeholder="Ex: 1.000.000"
                className="
                  w-full px-4 py-3
                  bg-[#0a0a0a] border border-[#333333]
                  text-white placeholder-[#888888]
                  rounded-lg
                  focus:outline-none focus:border-[#F4CC04]
                  disabled:opacity-50
                "
              />
              <p className="mt-1 text-xs text-[#888888]">
                Mínimo: 1.000 tokens. Define o máximo de tokens que o workspace pode consumir por mês
              </p>
            </div>
          </div>
        )}

        {/* Read-only message for non-TD users */}
        {!canEditTokenLimit && (
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4">
            <p className="text-xs text-[#888888]">
              O limite de tokens do workspace é gerenciado por usuários da TD
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceSettingsTab;
